import type { ReviewItem, ReviewStatus, ResponseStatus } from '../types';
import type { OfflineResponse, OfflineReview, PhotoQueueItem } from './offlineDatabase';

const parseJsonOrUndefined = <T>(value: string | null | undefined): T | undefined =>
  value ? JSON.parse(value) : undefined;

const parseJsonOrDefault = <T>(value: string | null | undefined, fallback: T): T =>
  value ? JSON.parse(value) : fallback;

const parseJsonOrOriginal = <T>(
  value: T,
  onParseError?: (error: unknown) => void
): T | unknown => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    onParseError?.(error);
    return value;
  }
};

const parseDateOrUndefined = (value: string | null | undefined): Date | undefined =>
  value ? new Date(value) : undefined;

export const mapRowToReview = (row: any): OfflineReview => ({
  id: row.id,
  tempId: row.temp_id,
  environmentKey: row.environment_key,
  propertyId: row.property_id,
  templateId: row.template_id,
  reviewerId: row.reviewer_id,
  status: row.status as ReviewStatus,
  startedAt: new Date(row.started_at),
  completedAt: parseDateOrUndefined(row.completed_at),
  submittedAt: parseDateOrUndefined(row.submitted_at),
  notes: row.notes,
  onDemandProjects: row.on_demand_projects,
  vendorNotes: row.vendor_notes,
  address: row.address,
  memberName: row.member_name,
  memberEmail: row.member_email,
  homeManagerName: row.home_manager_name,
  homeManagerEmail: row.home_manager_email,
  metadata: parseJsonOrUndefined(row.metadata),
  syncStatus: row.sync_status,
  lastSyncAttempt: parseDateOrUndefined(row.last_sync_attempt),
  syncError: row.sync_error,
  lastLocalMutationAt: parseDateOrUndefined(row.last_local_mutation_at),
  lastServerSeenAt: parseDateOrUndefined(row.last_server_seen_at),
  localRevision: row.local_revision ?? 0,
  serverRevision: row.server_revision ?? 0,
  conflictStatus: row.conflict_status || 'none',
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at),
});

export const mapRowToResponse = (row: any): OfflineResponse => ({
  id: row.id,
  environmentKey: row.environment_key,
  tempId: row.temp_id,
  reviewId: row.review_id,
  tempReviewId: row.temp_review_id,
  itemId: row.item_id,
  roomId: row.room_id,
  floor: row.floor,
  isPresent: Boolean(row.is_present),
  status: row.status as ResponseStatus,
  responseData: parseJsonOrUndefined(row.response_data),
  notes: row.notes,
  findings: parseJsonOrDefault(row.findings, []),
  syncStatus: row.sync_status,
  lastSyncAttempt: parseDateOrUndefined(row.last_sync_attempt),
  syncError: row.sync_error,
  lastLocalMutationAt: parseDateOrUndefined(row.last_local_mutation_at),
  lastServerSeenAt: parseDateOrUndefined(row.last_server_seen_at),
  localRevision: row.local_revision ?? 0,
  serverRevision: row.server_revision ?? 0,
  conflictStatus: row.conflict_status || 'none',
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at),
  itemSnapshot: parseJsonOrUndefined<ReviewItem>(row.item_snapshot),
});

export const mapRowToPhotoQueueItem = (row: any): PhotoQueueItem => ({
  id: row.id,
  environmentKey: row.environment_key,
  localPath: row.local_path,
  reviewResponseId: row.review_response_id,
  tempId: row.temp_response_id,
  fileName: row.file_name,
  mimeType: row.mime_type,
  size: row.size,
  caption: row.caption,
  note: row.note,
  metadata: parseJsonOrUndefined(row.metadata),
  status: row.status,
  retryCount: row.retry_count,
  errorMessage: row.error_message,
  priority: row.priority || 'normal',
  isHandwriting: Boolean(row.is_handwriting),
  presignedUrlExpiry: parseDateOrUndefined(row.presigned_url_expiry),
  processedForVision: Boolean(row.processed_for_vision),
  createdAt: new Date(row.created_at),
  lastAttemptAt: parseDateOrUndefined(row.last_attempt_at),
});

export const mapRowToPhotoMetadata = (row: any): any => ({
  id: row.id,
  reviewResponseId: row.response_id,
  s3Key: row.s3_key,
  s3Url: row.s3_url,
  fileName: row.file_name,
  mimeType: row.mime_type,
  size: row.size,
  caption: row.caption,
  note: row.note,
  noteSyncStatus: row.note_sync_status || 'synced',
  noteSyncError: row.note_sync_error,
  metadata: parseJsonOrDefault(row.metadata, {}),
  question_id: row.question_id,
  uploadedAt: parseDateOrUndefined(row.uploaded_at),
});

export const mapRowToIdMapping = (row: any): {
  tempId: string;
  realId: number;
  entityType: string;
  syncedAt: string;
} => ({
  tempId: row.temp_id,
  realId: row.real_id,
  entityType: row.entity_type,
  syncedAt: row.synced_at,
});

export const mapRowToOfflineOnDemandItem = (
  row: any,
  options: {
    normalizeSyncedId?: boolean;
    onQuestionsSchemaParseError?: (error: unknown) => void;
  } = {}
): any => {
  const item = {
    ...row,
    questions_schema: parseJsonOrOriginal(row.questions_schema, options.onQuestionsSchemaParseError),
  };

  if (options.normalizeSyncedId && typeof item.temp_id === 'string' && item.temp_id.startsWith('synced_')) {
    const serverId = parseInt(item.temp_id.replace('synced_', ''), 10);
    if (!isNaN(serverId)) {
      item.server_id = serverId;
      item.id = serverId;
    }
  }

  return item;
};

export const mapRowToSharedItemLink = (row: any): any => ({
  id: row.id,
  sourceItemId: row.source_item_id,
  targetItemId: row.target_item_id,
  includeRoom: row.include_room === 1,
  includeFloor: row.include_floor !== 0,
  includePhotos: row.include_photos === 1,
  version: row.version,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export const mapRowToQuestionMapping = (row: any): any => ({
  id: row.id,
  sharedItemLinkId: row.shared_item_link_id,
  sourceQuestionId: row.source_question_id,
  targetQuestionId: row.target_question_id,
  sharedKey: row.shared_key,
  validationRules: row.validation_rules ? JSON.parse(row.validation_rules) : null,
  version: row.version,
});

export const mapRowToCompletedResponse = (row: any): any => ({
  id: row.id,
  reviewId: row.review_id,
  itemId: row.item_id,
  roomId: row.room_id,
  floor: row.floor,
  isPresent: row.is_present === 1,
  notes: row.notes,
  status: row.status,
  responseData: parseJsonOrDefault(row.response_data, {}),
  photos: parseJsonOrDefault(row.photos, []),
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});
